import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  when,
} from '@/utils/validx-util'
import type { PhysicalAddress } from './PhysicalAddress'

/**
 * View model for editing a physical address.
 */
export class PhysicalAddressEditorViewModel {
  @observable private accessor _addressLine1 = ''
  @observable private accessor _addressLine2 = ''
  @observable private accessor _city = ''
  @observable private accessor _region = ''
  @observable private accessor _postalCode = ''
  @observable private accessor _country = 'US'

  readonly validation = validationContext(this, {
    addressLine1: [
      required('Address line 1 is required'),
      mustHaveMaxLength(100, 'Address line 1 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    addressLine2: [
      mustHaveMaxLength(100, 'Address line 2 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    city: [
      required('City is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'City must not exceed 100 characters'),
    ],
    region: [
      when(
        () => this.country === 'US',
        required('State or territory is required'),
      ),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Region must not exceed 100 characters'),
    ],
    postalCode: [
      required('Zip/postal code is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(20, 'Zip/postal code must not exceed 20 characters'),
    ],
    country: [required('Country is required')],
  })

  @computed get addressLine1() {
    return this._addressLine1
  }

  set addressLine1(value: string) {
    this._addressLine1 = value
    this.validation.clearErrors('addressLine1')
  }

  @computed get addressLine2() {
    return this._addressLine2
  }

  set addressLine2(value: string) {
    this._addressLine2 = value
    this.validation.clearErrors('addressLine2')
  }

  @computed get city() {
    return this._city
  }

  set city(value: string) {
    this._city = value
    this.validation.clearErrors('city')
  }

  @computed get region() {
    return this._region
  }

  set region(value: string) {
    this._region = value
    this.validation.clearErrors('region')
  }

  @computed get postalCode() {
    return this._postalCode
  }

  set postalCode(value: string) {
    this._postalCode = value
    this.validation.clearErrors('postalCode')
  }

  @computed get country() {
    return this._country
  }

  set country(value: string) {
    this._country = value
    this.validation.clearErrors('country')
  }

  /**
   * Resets the state of the view model to that of the given address, or
   * defaults if none is specified.
   */
  @action.bound
  reset(address?: PhysicalAddress) {
    this.addressLine1 = address?.addressLine1 ?? ''
    this.addressLine2 = address?.addressLine2 ?? ''
    this.city = address?.city ?? ''
    this.region = address?.region ?? ''
    this.postalCode = address?.postalCode ?? ''
    this.country = address?.country || 'US'
    this.validation.reset()
  }

  /**
   * Sets the state, province or region.
   */
  @action.bound
  setRegion(region: string) {
    this.region = region
  }

  /**
   * Sets the country code.
   */
  @action.bound
  setCountry(country: string) {
    this.country = country
  }

  /**
   * Maps the state of the VM to {@link PhysicalAddress}.
   */
  toPhysicalAddress(): PhysicalAddress {
    return {
      addressLine1: this.addressLine1,
      addressLine2: this.addressLine2,
      city: this.city,
      region: this.region,
      postalCode: this.postalCode,
      country: this.country,
    }
  }
}
